import { EventEmitter } from 'events';
import type { UA } from 'jssip';
import JsSIP from 'jssip/lib/JsSIP';
import RequestSender from 'jssip/lib/RequestSender';
import type {
	ReferEvent,
	ReInviteEvent,
	ReinviteRequestPendingEvent,
	RTCSession,
} from 'jssip/lib/RTCSession';
import { IncomingRequest, OutgoingRequest } from 'jssip/lib/SIPMessage';
import type {
	CallOptions,
	NotifierOptions,
	RegisteredEvent,
	RequestParams,
	SubscriberOptions,
} from 'jssip/lib/UA';
import type { URI } from 'jssip/lib/URI';

import { DEFAULT_PARK_URI, ICE_SERVERS } from '../config';
import type {
	ConferenceInfo,
	EventPackageHandler,
	JsSipConfig,
	NotifyPayload,
	ParkedDialog,
	ParkFailureEvent,
	ParkProgressEvent,
	RegSnapshot,
	SessionInfo,
	SignalQuality,
	SipEventMeta,
	SipRequest,
	SipTarget,
	SubscriptionTerminatedPayload,
	WatchPreference,
	WatchSubscriberOptions,
} from '../model/types';
import {
	buildReplacesParam,
	getCallId,
	getLocalRemoteTags,
	getRemoteContactUri,
	PARK_ACCEPT,
	PARK_EVENT,
	parseSipfragStatus,
	parseSubscriptionState,
} from './call-parking-utils';
import {
	CONFERENCE_ACCEPT,
	CONFERENCE_EVENT,
	parseContactUriFromResponse,
} from './conference-info-utils';
import { createEarlyIceReadyController } from './ice-candidates';
import { NOTIFY_HANDLED_FLAG, patchMethod, patchSession } from './jssip-patcher';
import {
	applyDirectionalPolicy,
	categorizeQuality,
	ensureCodec,
	getDirection,
	getTargetTransceiver,
	isPortZero,
	isRemoteSending,
} from './media';
import {
	buildReferHeaders,
	getIdentityFromHeader,
	makeReferTarget,
	normalizeEventKey,
	safePositive,
	shouldRetrySubscriptionTermination,
} from './misc';
import { REG_EVENT } from './reg-utils';
import {
	registerCallParkingPackage,
	registerConferencePackage,
	registerRegPackage,
} from './sip-events';

const TEL_URI_SCHEME_REGEXP = /^tel:/i;

type JsSipUaWithUriCallTarget = UA & {
	call: (target: string | URI, options?: CallOptions) => RTCSession;
	normalizeTarget: (target: string) => URI | undefined;
};

const toJsSipCallTarget = (target: string, ua: JsSipUaWithUriCallTarget): string | URI => {
	const normalizedTarget = target.trim();

	if (!TEL_URI_SCHEME_REGEXP.test(normalizedTarget)) {
		return target;
	}

	const sipTarget = ua.normalizeTarget(normalizedTarget);
	if (!sipTarget) {
		throw new TypeError(`Invalid target: ${target}`);
	}

	sipTarget.setParam('user', 'phone');
	return sipTarget;
};

/**
 * Обёртка над JsSIP.UA: управляет звонками, подписками, парковкой и качеством связи.
 */
export class JsSIPAdapter extends EventEmitter {
	private ua: UA;
	private sessions: Map<string, SessionInfo> = new Map();
	private destroyed = false;
	private subscribers = new Map<string, any>(); // key: `${target}|${event}`
	private notifiers = new Set<any>();
	private regState = new Map<string, RegSnapshot>(); // target AOR -> snapshot
	private conferenceState = new Map<string, ConferenceInfo>(); // target AOR -> snapshot
	private eventPackages = new Map<string, EventPackageHandler>();
	private watchPreferences = new Map<string, WatchPreference>();
	private resubscribeTimers = new Map<string, ReturnType<typeof setTimeout>>();
	private reinviteRetryTimers = new Map<string, ReturnType<typeof setTimeout>>();
	private parkSubscriptions = new Map<string, { sessionId: string; target: string }>();
	private referSubscriptions = new Map<string, { requestUri: string; referTo: SipTarget }>();

	constructor(private config: JsSipConfig) {
		super();
		if (process.env.NODE_ENV === 'production') {
			JsSIP.debug.disable();
		} else {
			JsSIP.debug.enable('JsSIP:*');
		}

		const socket = new JsSIP.WebSocketInterface(config.websocketUrl);
		this.ua = new JsSIP.UA({
			uri: config.uri,
			display_name: config.displayName,
			password: config.password,
			sockets: [socket],
			session_timers: config.sessionTimers ?? false,
			session_timers_refresh_method: config.sessionTimersRefreshMethod,
			connection_recovery_min_interval: 1,
			connection_recovery_max_interval: 20,
			registrar_server: config.registrarServer,
			register: true,
		});
		patchMethod();

		this.bindPackagesEvent();
		this.bindUaEvents(config.autoReconnect);
	}

	/* -------------- Public API -------------- */
	/** Запускает UA и открывает WebSocket */
	public start() {
		if (this.destroyed) return;
		this.ua.start();
	}

	/** Полностью останавливает UA и очищает ресурсы */
	public stop() {
		this.destroy();
	}

	/** Инициирует REGISTER */
	public register() {
		if (this.destroyed) return;
		this.ua.register();
	}

	/** Отправляет unREGISTER */
	public unregister() {
		if (this.destroyed) return;
		this.ua.unregister();
	}

	/** Исходящий вызов */
	public call(target: string, mediaConstraints?: MediaStreamConstraints, extraHeaders?: string[]) {
		const constraints = mediaConstraints ?? { audio: true, video: false };
		const opts: CallOptions = {
			mediaConstraints: constraints, // сначала без видео
			pcConfig: {
				iceServers: ICE_SERVERS,
				rtcpMuxPolicy: 'require',
			},
			extraHeaders,
			eventHandlers: {
				peerconnection({ peerconnection }) {
					ensureCodec(peerconnection);
				},
			},
		};
		const ua = this.ua as JsSipUaWithUriCallTarget;
		return ua.call(toJsSipCallTarget(target, ua), opts);
	}

	/** Ответ на входящий */
	public answer(id: string, mediaConstraints?: MediaStreamConstraints) {
		const info = this.sessions.get(id);
		if (!info || info.session.direction !== 'incoming') return;

		const constraints = mediaConstraints ?? { audio: true, video: false };
		const opts: CallOptions = {
			mediaConstraints: constraints,
			pcConfig: {
				iceServers: ICE_SERVERS,
				rtcpMuxPolicy: 'require',
			},
			eventHandlers: {
				peerconnection({ peerconnection }) {
					ensureCodec(peerconnection);
				},
			},
		};
		info.session.answer(opts);
	}

	/** Завершает звонок по sessionId */
	public hangup(id: string) {
		const info = this.sessions.get(id);
		info?.session.terminate();
	}

	/** Отменяет исходящий вызов, если он ещё устанавливается */
	public cancel(id: string): void {
		const info = this.sessions.get(id);
		if (info?.session.isInProgress()) {
			info.session.terminate({ status_code: 487, reason_phrase: 'Cancel' });
		}
	}

	/* ------------------- AUDIO ------------------- */
	/**
	 * Выключает микрофон для сессии: меняет направление трансивера и отправляет renegotiation.
	 *
	 * @param id Идентификатор сессии.
	 */
	public async muteAudio(id: string): Promise<void> {
		const info = this.sessions.get(id);
		if (!info) return;

		if (info.isOnHold.remote) {
			info.session.mute({ audio: true });
			return;
		}

		const pc = info.session.connection as RTCPeerConnection;
		if (!pc) return;

		const txAudio = pc.getTransceivers().find(getTargetTransceiver('audio'));
		if (!txAudio) return;

		const remoteSending = isRemoteSending(txAudio.currentDirection);
		txAudio.direction = remoteSending ? 'recvonly' : 'inactive';

		// Включаем локальное аудио
		info.session.mute({ audio: true });

		// Запускаем renegotiation для добавления audio m-line
		if (info.session.isEstablished()) {
			// useUpdate=true отправит UPDATE, иначе будет Re-INVITE
			await info.session.renegotiate({
				useUpdate: false,
				rtcOfferConstraints: { iceRestart: true },
			});
		}
	}

	/**
	 * Включает микрофон для сессии и обновляет направление трансивера.
	 *
	 * @param id Идентификатор сессии.
	 */
	public async unmuteAudio(id: string) {
		const info = this.sessions.get(id);
		if (!info) return;

		if (info.isOnHold.remote) {
			info.session.unmute({ audio: true });
			return;
		}

		const pc = info.session.connection as RTCPeerConnection;
		if (!pc) return;

		const txAudio = pc.getTransceivers().find(getTargetTransceiver('audio'));
		if (!txAudio) return;

		const remoteSending = isRemoteSending(txAudio.currentDirection);
		// Если удалённый по-прежнему молчит → sendonly, иначе sendrecv
		txAudio.direction = remoteSending ? 'sendrecv' : 'sendonly';

		// Включаем локальное аудио
		info.session.unmute({ audio: true });

		// Запускаем renegotiation для добавления audio m-line
		if (info.session.isEstablished()) {
			// useUpdate=true отправит UPDATE, иначе будет Re-INVITE
			await info.session.renegotiate({
				useUpdate: false,
				rtcOfferConstraints: { iceRestart: true },
			});
		}
	}

	/* ------------------- VIDEO ------------------- */
	/**
	 * Выключает исходящее видео: ставит трансивер в recvonly/inactive и делает renegotiation.
	 *
	 * @param id Идентификатор сессии.
	 */
	public async muteVideo(id: string): Promise<void> {
		const info = this.sessions.get(id);
		if (!info) return;

		const pc = info.session.connection as RTCPeerConnection;
		if (!pc) return;

		const videoTx = pc.getTransceivers().find(getTargetTransceiver('video'));
		if (!videoTx) return;

		const remoteSending = isRemoteSending(videoTx.currentDirection);
		videoTx.direction = remoteSending ? 'recvonly' : 'inactive';

		info.session.mute({ video: true });
		// TODO подумать удалять треки, а не просто выключать
		this.emit('localStream', info.localStream); // UI покажет «выкл.»

		// Запускаем renegotiation для добавления audio m-line
		if (info.session.isEstablished()) {
			// useUpdate=true отправит UPDATE, иначе будет Re-INVITE
			await info.session.renegotiate({
				useUpdate: false,
				rtcOfferConstraints: { iceRestart: true },
			});
		}
	}

	/**
	 * Включает видео, подменяя текущий трек и обновляя направления трансиверов.
	 *
	 * @param id Идентификатор сессии.
	 * @param newTrack Новый видеотрек (камера/экран).
	 */
	public async unmuteVideo(id: string, newTrack: MediaStreamTrack) {
		const info = this.sessions.get(id);
		if (!info) return;

		const pc = info.session.connection as RTCPeerConnection;
		if (!pc) return;

		const videoTx = pc.getTransceivers().find(getTargetTransceiver('video'));

		if (videoTx) {
			await videoTx.sender.replaceTrack(newTrack);
			const remoteSending = isRemoteSending(videoTx.currentDirection);
			// Если удалённый по-прежнему молчит → sendonly, иначе sendrecv
			videoTx.direction = remoteSending ? 'sendrecv' : 'sendonly';
		} else {
			pc.addTransceiver(newTrack, { direction: 'sendonly' });
		}

		info.session.unmute({ video: true });

		info.localStream.getVideoTracks().forEach((t) => info.localStream.removeTrack(t));
		info.localStream.addTrack(newTrack);
		this.emit('localStream', info.localStream);

		// Запускаем renegotiation для добавления audio m-line
		if (info.session.isEstablished()) {
			// useUpdate=true отправит UPDATE, иначе будет Re-INVITE
			await info.session.renegotiate({
				useUpdate: false,
				rtcOfferConstraints: { iceRestart: true },
			});
		}
	}

	/**
	 * Включает трансляцию экрана вместо текущего видео-трека.
	 *
	 * @param id Идентификатор сессии.
	 * @param stream MediaStream с видеотреком экрана.
	 */
	public async shareScreen(id: string, stream: MediaStream) {
		const info = this.sessions.get(id);
		if (!info) return;

		const pc = info.session.connection as RTCPeerConnection;
		if (!pc) return;

		const newTrack = stream.getVideoTracks()[0];
		newTrack.contentHint = 'detail';

		const videoTx = pc.getTransceivers().find(getTargetTransceiver('video'));

		if (videoTx) {
			await videoTx.sender.replaceTrack(newTrack);
			const remoteSending = isRemoteSending(videoTx.currentDirection);
			// Если удалённый по-прежнему молчит → sendonly, иначе sendrecv
			videoTx.direction = remoteSending ? 'sendrecv' : 'sendonly';
		} else {
			pc.addTransceiver(newTrack, { direction: 'sendrecv' });
		}

		info.session.unmute({ video: true });

		info.localStream.getVideoTracks().forEach((t) => info.localStream.removeTrack(t));
		info.localStream.addTrack(newTrack);
		this.emit('localStream', info.localStream);

		if (info.session.isEstablished()) {
			await info.session.renegotiate({
				useUpdate: false,
				rtcOfferConstraints: { iceRestart: true },
			});
		}
	}

	/* ------------------- HOLD / UNHOLD ------------------- */
	/**
	 * Ставит звонок на удержание: останавливает трансиверы и отправляет renegotiation.
	 *
	 * @param id Идентификатор сессии.
	 */
	public async hold(id: string): Promise<void> {
		const info = this.sessions.get(id);
		if (!info) return;

		const pc = info.session.connection as RTCPeerConnection;
		if (!pc) return;

		pc.getTransceivers().forEach((tx) => {
			if (getTargetTransceiver('audio')(tx) || getTargetTransceiver('video')(tx)) {
				tx.stop(); // ← ключевая операция
			}
		});

		info.session.mute({ video: true });
		this.emit('localStream', info.localStream); // UI покажет «выкл.»

		info.isOnHold.local = true;
		this.emit('hold', id);

		// Запускаем renegotiation для добавления audio m-line
		if (info.session.isEstablished()) {
			// useUpdate=true отправит UPDATE, иначе будет Re-INVITE
			await info.session.renegotiate({
				useUpdate: false,
				rtcOfferConstraints: { iceRestart: false },
			});
		}
	}

	/**
	 * Снимает звонок с удержания и восстанавливает медиа-треки.
	 *
	 * @param id Идентификатор сессии.
	 */
	public async unhold(id: string): Promise<void> {
		const info = this.sessions.get(id);
		if (!info) return;

		const pc = info.session.connection as RTCPeerConnection;
		if (!pc) return;

		this.restoreTracks(info);

		info.isOnHold.local = false;
		this.emit('unhold', info.id);

		// Снимаем удержание
		if (info.session.isEstablished()) {
			// useUpdate=true отправит UPDATE, иначе будет Re-INVITE
			await info.session.renegotiate({
				useUpdate: false,
				rtcOfferConstraints: { iceRestart: true },
			});
		}
	}

	/* ------------------- Refer ------------------- */
	/**
	 * Инициирует REFER на другой URI и завершает текущий звонок после 202.
	 *
	 * @param id Идентификатор сессии.
	 * @param target Целевой URI или номер для перевода.
	 */
	public transferCall(id: string, target: SipTarget) {
		const info = this.sessions.get(id);
		if (!info?.session) return;

		const options = {
			extraHeaders: buildReferHeaders({
				referToUri: target,
				selfUri: makeReferTarget(this.getSelfUri(), this.config.displayName),
				requireReplaces: true,
				disableReferSubscription: true,
			}),
			eventHandlers: {
				// 1. Сюда прилетает 202 Accepted
				requestSucceeded: (e: RegisteredEvent) => {
					if (e.response && e.response.status_code === 202) {
						this.hangup(info.id);
					}
				},
				// 2. Ошибки (408, 500…)
				requestFailed: (e: any) => {
					console.error('REFER failed', e);
				},
			},
		};

		info.session.refer(target.aor, options);
	}

	/**
	 * Отправляет REFER для парковки текущего вызова.
	 *
	 * @param id Идентификатор сессии.
	 * @param displayName Имя пользователя, которого хотим припарковать.
	 */
	public parkCall(id: string, displayName?: string) {
		const info = this.sessions.get(id);
		if (!info?.session) return;

		const { session } = info;

		const originalCallId = getCallId(session);
		const { localTag, remoteTag } = getLocalRemoteTags(session);
		if (!originalCallId || !localTag || !remoteTag) {
			console.warn('[Park] cannot determine call identifiers');
			return;
		}

		const remoteContact = getRemoteContactUri(session);
		const replacesParam = buildReplacesParam(originalCallId, localTag, remoteTag);

		const referTo = `${remoteContact}?Replaces=${replacesParam}&Require=replaces`;

		const headers = buildReferHeaders({
			referToUri: makeReferTarget(referTo, displayName),
			includeEventRefer: true,
			requireReplaces: true,
			includeSupported: true,
			selfUri: makeReferTarget(this.getSelfUri(), this.config.displayName),
			contactUri: this.getContactUri(),
		});

		// TODO подумать и переписать логику
		this.emitParkProgress(id, { stage: 'trying' });

		const callId = this.sendStandaloneRefer(DEFAULT_PARK_URI, headers, {
			beforeSend: (request: any) => {
				this.parkSubscriptions.set(request.call_id, { sessionId: id, target: DEFAULT_PARK_URI });
			},
			onTrying: (code, reason) => {
				this.emitParkProgress(id, {
					stage: code && code > 100 ? 'progress' : 'trying',
					code,
					reason,
				});
			},
			onProgress: (code, reason) => {
				this.emitParkProgress(id, { stage: 'progress', code, reason });
			},
			onAccepted: (code, reason) => {
				this.emitParkProgress(id, { stage: 'accepted', code, reason });
			},
			onFailed: (code, reason) => {
				this.parkSubscriptions.delete(callId ?? '');
				this.emitParkFailure(id, { code, reason: reason || 'REFER failed' });
			},
			onTimeout: () => {
				if (callId) this.parkSubscriptions.delete(callId);
				this.emitParkFailure(id, { code: 408, reason: 'Request timeout' });
			},
			onTransportError: () => {
				if (callId) this.parkSubscriptions.delete(callId);
				this.emitParkFailure(id, { reason: 'Transport error' });
			},
		});
	}

	/**
	 * Возобновляет припаркованный диалог через Replaces.
	 *
	 * @param dialog Данные припаркованного диалога.
	 * @param mediaConstraints Настройки медиа.
	 */
	public resumeParkedDialog(dialog: ParkedDialog, mediaConstraints?: MediaStreamConstraints) {
		if (!dialog.callId || !dialog.localTag || !dialog.remoteTag) {
			throw new Error('Incomplete dialog information for call retrieve');
		}

		const target = dialog.remoteTarget || dialog.localTarget;
		if (!target) {
			throw new Error('Dialog info does not include target URI');
		}
		const replacesParam = `${dialog.callId};from-tag=${dialog.localTag};to-tag=${dialog.remoteTag}`;

		const extraHeaders = buildReferHeaders({
			replaces: replacesParam,
			requireReplaces: true,
			includeSupported: true,
		});

		return this.call(target, mediaConstraints, extraHeaders);
	}

	/* ---------- SUBSCRIBE (outgoing) ---------- */
	/**
	 * Создаёт и активирует исходящую SIP SUBSCRIBE с биндингом на внутренние события.
	 *
	 * @param target Целевой AOR.
	 * @param eventName Имя event-package.
	 * @param accept Заголовок Accept (строка или массив).
	 * @param options Дополнительные опции подписки.
	 */
	public subscribe(target: string, eventName: string, accept: string, options?: SubscriberOptions) {
		const sub = this.ua.subscribe(target, eventName, accept, options);
		sub.subscribe();
		this.bindSubscriberEvents(sub, { target, eventName });

		const key = `${target}|${eventName}`;
		this.subscribers.set(key, sub);

		return sub;
	}

	/**
	 * Завершает подписку и удаляет её из реестра.
	 *
	 * @param target Целевой AOR.
	 * @param eventName Имя event-package.
	 */
	public unsubscribe(target: string, eventName: string) {
		const key = `${target}|${eventName}`;
		const sub = this.subscribers.get(key);

		if (sub) {
			try {
				sub.terminate?.();
			} catch {
				console.error('REFER unsubscribe', target, eventName);
			}
			this.subscribers.delete(key);
		}
	}

	/* ---------- NOTIFIER (in response to incoming SUBSCRIBE) ---------- */
	/**
	 * Создаёт NOTIFY-отправителя для входящего SUBSCRIBE и вешает обработчики.
	 *
	 * @param subscribeRequest Исходный запрос SUBSCRIBE.
	 * @param contentType Тип содержимого NOTIFY.
	 * @param options Дополнительные опции.
	 */
	public createNotifier(
		subscribeRequest: IncomingRequest,
		contentType: string,
		options?: NotifierOptions
	) {
		const notifier = this.ua.notify(subscribeRequest, contentType, options);
		this.notifiers.add(notifier); // <-- добавить
		this.bindNotifierEvents(notifier);
		return notifier;
	}

	/* ------------------- OTHER PUBLIC ------------------- */
	/**
	 * Рассчитывает качество сигнала для указанной сессии по WebRTC-статистике.
	 *
	 * @param id Идентификатор сессии.
	 * @throws Если сессия или PC не найдены.
	 */
	public async getSignalQuality(id: string): Promise<SignalQuality> {
		const info = this.sessions.get(id);
		if (!info) throw new Error(`Session ${id} not found`);

		const pc = info.session.connection as RTCPeerConnection;
		if (!pc) throw new Error(`Not PC ${id} not found`);

		const stats = await pc.getStats();

		// Активные медиатреки
		const activeReceivers = pc
			.getReceivers()
			.filter((r) => r.track && r.track.enabled && r.track.readyState === 'live');

		const activeSenders = pc
			.getSenders()
			.filter((s) => s.track && s.track.enabled && s.track.readyState === 'live');

		if (activeReceivers.length === 0 && activeSenders.length === 0) {
			return 'bad'; // звонок есть, но медиа отключены
		}

		/* ---------- агрегаторы ---------- */
		let lossAudio = 0;
		let packetsAudio = 0;
		let jitterMs = 0;
		let lossVideo = 0;
		let packetsVideo = 0;
		let dropCnt = 0;
		let frameCnt = 0;
		let rttMs = 0;

		/* ---------- проход по Stats ---------- */
		stats.forEach((r) => {
			if (r.type === 'inbound-rtp') {
				const lost = safePositive(r.packetsLost);
				const recv = safePositive(r.packetsReceived);
				if (r.kind === 'audio') {
					lossAudio += lost;
					packetsAudio += lost + recv;
					jitterMs = Math.max(jitterMs, safePositive(r.jitter) * 1000);
				}
				if (r.kind === 'video') {
					lossVideo += lost;
					packetsVideo += lost + recv;
					frameCnt += safePositive(r.framesDecoded);
					dropCnt += safePositive(r.framesDropped);
				}
			}
			if (r.type === 'remote-inbound-rtp') {
				const lost = safePositive(r.packetsLost);
				const recv = safePositive(r.packetsReceived);
				if (r.kind === 'audio') {
					lossAudio += lost;
					packetsAudio += lost + recv;
				}
				if (r.kind === 'video') {
					lossVideo += lost;
					packetsVideo += lost + recv;
				}
			}
			if (r.type === 'candidate-pair' && r.nominated && r.state === 'succeeded') {
				rttMs = (r.currentRoundTripTime ?? 0) * 1000;
			}
		});

		return categorizeQuality(
			packetsAudio,
			lossAudio,
			packetsVideo,
			lossVideo,
			frameCnt,
			dropCnt,
			jitterMs,
			rttMs
		);
	}

	/**
	 * Поменять на лету:
	 *  • запрашиваем новый трек по deviceId
	 *  • заменяем его в RTCRtpSender
	 *  • обновляем локальный preview-поток и UI
	 */
	public async switchMediaInput(id: string, newTrack: MediaStreamTrack): Promise<void> {
		const info = this.sessions.get(id);
		if (!info) return;

		const pc = info.session.connection as RTCPeerConnection;
		if (!pc) return;

		const senders = pc.getSenders();
		const sender = senders.find((s) => s.track?.kind === newTrack.kind) || null;
		if (!sender) return;

		// Заменяем трек в PeerConnection
		await sender.replaceTrack(newTrack);

		// Обновляем локальный preview (если нужен)
		//    — удаляем старый и добавляем новый трек в info.localStream
		// Обновляем локальный preview только по конкретному kind
		const stream = info.localStream;
		const isAudio = newTrack.kind === 'audio';
		const remove = isAudio ? stream.getAudioTracks() : stream.getVideoTracks();
		remove.forEach((t) => {
			t.stop();
			stream.removeTrack(t);
		});
		stream.addTrack(newTrack);
		this.emit('localStream', stream);
	}

	/** Посылает DTMF-тоны через активную сессию */
	public sendDTMF(id: string, tones: string) {
		this.sessions.get(id)?.session.sendDTMF(tones);
	}

	/** Возвращает первую активную (не на холде) сессию */
	public getCurrentSession() {
		return [...this.sessions.values()].find((info) => !info.isOnHold.local);
	}

	/** Возвращает копию списка всех сессий */
	public getAllSessions(): SessionInfo[] {
		return [...this.sessions.values()];
	}

	/** Отправляет REFER с Replaces (новый Call-ID) на указанный URI. */
	public referWithReplaces(
		targetUri: string,
		targetDialogSessionId: string,
		replaceSessionId: string,
		replaceDisplayName?: string
	) {
		const replaceInfo = this.sessions.get(replaceSessionId);
		const targetDialogInfo = this.sessions.get(targetDialogSessionId);

		if (!replaceInfo?.session || !targetDialogInfo?.session || !targetUri) return;

		const replaceSession = replaceInfo.session;
		const callId = getCallId(replaceSession);
		const { localTag, remoteTag } = getLocalRemoteTags(replaceSession);

		const targetDialogSession = targetDialogInfo.session;
		const tdCallId = getCallId(targetDialogSession);
		const { localTag: tdLocalTag, remoteTag: tdRemoteTag } =
			getLocalRemoteTags(targetDialogSession);

		if (!callId || !localTag || !remoteTag || !tdCallId || !tdLocalTag || !tdRemoteTag) return;

		const targetDialog = `${tdCallId};local-tag=${tdLocalTag};remote-tag=${tdRemoteTag}`;
		const replaceParam = buildReplacesParam(callId, localTag, remoteTag);

		const { aor } = replaceInfo;
		const referTo = `${aor}?Replaces=${replaceParam}&Require=replaces`;

		const headers = buildReferHeaders({
			referToUri: makeReferTarget(referTo, replaceDisplayName),
			requireReplaces: true,
			requireTdialog: true,
			targetDialog,
			includeSupported: true,
			selfUri: makeReferTarget(this.getSelfUri(), this.config.displayName),
			contactUri: this.getContactUri(),
		});

		this.sendStandaloneRefer(targetUri, headers);
	}

	/** Получает SessionInfo по id, если есть */
	public getSessionById(id: string) {
		return this.getAllSessions().find((ses) => ses.id === id);
	}

	/** Возвращает SIP Call-ID для сессии по sessionId. */
	public getSessionCallId(id: string): string | null {
		const info = this.sessions.get(id);
		if (!info) return null;

		return getCallId(info.session) ?? null;
	}

	/** Простое REFER на targetUri (новый Call-ID). */
	public referTarget(targetUri: string, referTo: SipTarget, targetDialogSessionId: string) {
		const targetDialogInfo = this.sessions.get(targetDialogSessionId);

		if (!targetDialogInfo?.session) return;
		const targetDialogSession = targetDialogInfo.session;
		const tdCallId = getCallId(targetDialogSession);
		const { localTag: tdLocalTag, remoteTag: tdRemoteTag } =
			getLocalRemoteTags(targetDialogSession);
		if (!tdCallId || !tdLocalTag || !tdRemoteTag) return;
		const targetDialog = `${tdCallId};local-tag=${tdLocalTag};remote-tag=${tdRemoteTag}`;

		const { aor, displayName } = referTo;
		if (!targetUri || !aor) return;

		const headers = buildReferHeaders({
			referToUri: makeReferTarget(aor, displayName),
			selfUri: makeReferTarget(this.getSelfUri(), this.config.displayName),
			contactUri: this.getContactUri(),
			requireTdialog: true,
			targetDialog,
			includeSupported: true,
		});

		let referCallId: string | undefined;

		const callId = this.sendStandaloneRefer(targetUri, headers, {
			beforeSend: (request: any) => {
				referCallId = request.call_id;
				this.referSubscriptions.set(request.call_id, {
					requestUri: targetUri,
					referTo: { aor, displayName },
				});
			},
		});

		if (!referCallId && callId) {
			this.referSubscriptions.set(callId, {
				requestUri: targetUri,
				referTo: { aor, displayName },
			});
		}
	}

	/** Проверяет флаг готовности к повторной отправке offer */
	public isReadyToReOffer(id: string) {
		const info = this.sessions.get(id);
		if (!info?.session) return;

		if (info.session.isEstablished()) {
			return info.session.isReadyToReOffer();
		}
		return false;
	}

	/** Возвращает, открыт ли WebSocket и готов ли UA */
	public isConnected() {
		return this.ua.isConnected();
	}

	/** Проверяет, зарегистрирован ли UA в текущий момент */
	public isRegistered() {
		return this.ua.isRegistered();
	}

	/** Возвращает полный SIP URI пользователя */
	// формат 'sip:test0003@s2.itgold.io'
	public getSelfUri(): string {
		const innerUri = (this.ua as any).configuration.uri?.toString?.();
		if (innerUri) {
			return innerUri;
		}

		return this.config.uri;
	}

	/** Хелпер: 'alice' -> 'sip:alice@<domain>' */
	public normalizeAor(aorOrUser: string): string {
		// TODO Проверить и упростить
		const normalizedInput = aorOrUser?.trim();
		if (!normalizedInput) return '';

		const withoutBrackets = normalizedInput.replaceAll('<', '').replaceAll('>', '');
		const withoutParams = withoutBrackets.split('?')[0].split(';')[0].trim();
		const withoutScheme = withoutParams.replace(/^sip:/i, '').trim();

		if (!withoutScheme) return '';

		const [userRaw, hostRaw] = withoutScheme.split('@');
		const user = userRaw?.trim();
		const host = hostRaw?.trim();

		if (!user) {
			return withoutParams;
		}

		if (host) {
			return new JsSIP.URI('sip', user, host).toString();
		}

		const uaUri = (this.ua as any).configuration.uri as JsSIP.URI; // JsSIP.URI
		const domain = uaUri?.host;
		if (!domain) {
			return /^sip:/i.test(withoutParams) ? `sip:${user}` : user;
		}

		const uri = new JsSIP.URI('sip', user, domain);
		return uri.toString();
	}

	/**
	 * Создаёт/возобновляет подписку с авто‑ретраями и кэшированием предпочтений.
	 *
	 * @param target SIP AOR, например `sip:alice@example.com`.
	 * @param eventName Имя event-package.
	 * @param options Опции подписчика и поведения автоподписки.
	 */
	public watchSubscriber(target: string, eventName: string, options?: WatchSubscriberOptions) {
		const aor = this.normalizeAor(target);
		const keyName = `${aor}|${eventName}`;
		const eventKey = normalizeEventKey(eventName);

		const handler = this.eventPackages.get(eventKey);
		const accept = options?.accept ?? handler?.accept ?? 'application/simple-message-summary';
		const autoRetryRequested = options?.autoRetry === true;
		const autoRetryDisabled = options?.autoRetry === false;
		const retryDelayMs = options?.retryDelayMs;

		let subscribeOptions: SubscriberOptions | undefined;
		if (options) {
			const {
				accept: _accept,
				autoRetry: _autoRetry,
				retryDelayMs: _retryDelayMs,
				...rest
			} = options;
			if (Object.keys(rest).length > 0) {
				subscribeOptions = rest;
			}
		}

		if (autoRetryRequested) {
			this.watchPreferences.set(keyName, {
				target: aor,
				accept,
				options: subscribeOptions,
				autoRetry: true,
				retryDelayMs,
			});
		} else if (autoRetryDisabled) {
			this.watchPreferences.delete(keyName);
		}

		this.clearResubscribeTimer(keyName);

		// уже есть активная подписка?
		const existing = this.subscribers.get(keyName);
		if (existing) return existing;

		return this.subscribe(aor, eventName, accept, subscribeOptions);
	}

	/**
	 * Останавливает подписку на указанный event-package и очищает авто‑retry для неё.
	 *
	 * @param target SIP AOR, например `sip:alice@example.com`.
	 * @param eventName Имя event-package.
	 */
	public unwatchSubscriber(target: string, eventName: string) {
		const aor = this.normalizeAor(target);
		const keyName = `${aor}|${eventName}`;
		this.watchPreferences.delete(keyName);
		this.clearResubscribeTimer(keyName);
		this.unsubscribe(aor, eventName);
		if (normalizeEventKey(eventName) === REG_EVENT) {
			this.regState.delete(aor);
		}
	}

	/**
	 * Упрощённая подписка на `reg`-события.
	 *
	 * @param target SIP AOR, например `sip:alice@example.com`.
	 * @param options Дополнительные параметры подписки.
	 */
	public watchRegister(target: string, options?: WatchSubscriberOptions) {
		const merged: WatchSubscriberOptions = {
			...options,
		};

		return this.watchSubscriber(target, REG_EVENT, merged);
	}

	/**
	 * Останавливает подписку на `reg`-события для target.
	 *
	 * @param target SIP AOR, например `sip:alice@example.com`.
	 */
	public unwatchRegister(target: string) {
		this.unwatchSubscriber(target, REG_EVENT);
	}

	/**
	 * Упрощённая подписка на событие парковки (`Event: dialog`).
	 *
	 * @param target SIP AOR, например `sip:alice@example.com`.
	 * @param options Дополнительные параметры подписки.
	 */
	public watchCallParking(target: string, options?: WatchSubscriberOptions) {
		const merged: WatchSubscriberOptions = {
			accept: PARK_ACCEPT,
			...options,
		};
		return this.watchSubscriber(target, PARK_EVENT, merged);
	}

	/**
	 * Останавливает подписку на событие парковки для target.
	 *
	 * @param target SIP AOR, например `sip:alice@example.com`.
	 */
	public unwatchCallParking(target: string) {
		this.unwatchSubscriber(target, PARK_EVENT);
	}

	/**
	 * Упрощённая подписка на конференцию (`Event: conference`).
	 *
	 * @param target SIP AOR, например `sip:alice@example.com`.
	 * @param options Дополнительные параметры подписки.
	 */
	public watchConference(target: string, options?: WatchSubscriberOptions) {
		const merged: WatchSubscriberOptions = {
			accept: CONFERENCE_ACCEPT,
			...options,
		};
		return this.watchSubscriber(target, CONFERENCE_EVENT, merged);
	}

	/**
	 * Останавливает подписку на конференцию и очищает локальный snapshot.
	 *
	 * @param target SIP AOR, например `sip:alice@example.com`.
	 */
	public unwatchConference(target: string) {
		this.unwatchSubscriber(target, CONFERENCE_EVENT);
		this.setConferenceSnapshot(target, null);
	}

	/**
	 * Возвращает последний snapshot регистрации для target, если он есть.
	 *
	 * @param target SIP AOR, например `sip:alice@example.com`.
	 */
	public getRegistrationSnapshot(target: string): RegSnapshot | null {
		return this.regState.get(this.normalizeAor(target)) ?? null;
	}

	/**
	 * Сохраняет snapshot регистрации для указанного AOR (или удаляет, если null).
	 *
	 * @param target SIP AOR, например `sip:alice@example.com`.
	 * @param snap Текущее состояние регистрации или null для очистки.
	 */
	public setRegistrationSnapshot(target: string, snap: RegSnapshot | null) {
		const key = this.normalizeAor(target);
		if (snap === null) this.regState.delete(key);
		else this.regState.set(key, snap);
	}

	/**
	 * Возвращает последний snapshot conference-info для AOR, если он есть.
	 *
	 * @param target SIP AOR, например `sip:alice@example.com`.
	 */
	public getConferenceSnapshot(target: string): ConferenceInfo | null {
		return this.conferenceState.get(this.normalizeAor(target)) ?? null;
	}

	/**
	 * Обновляет кеш конференции и эмитит события UI.
	 *
	 * @param target SIP AOR, например `sip:alice@example.com`.
	 * @param snap Новое состояние conference-info или null для очистки.
	 * @param payload Полезная нагрузка NOTIFY (для признака final).
	 */
	public setConferenceSnapshot(
		target: string,
		snap: ConferenceInfo | null,
		payload?: NotifyPayload
	) {
		const key = this.normalizeAor(target);
		if (snap === null) {
			this.conferenceState.delete(key);
			this.emit('conferenceSubscriptionTerminated', target);
		} else {
			this.conferenceState.set(key, snap);
			this.emit('conferenceInfoUpdated', target, snap, payload?.isFinal);
		}
	}

	/**
	 * Регистрирует обработчик кастомного event-package.
	 *
	 * @param eventName Имя event-package.
	 * @param handler Набор обработчиков жизненного цикла подписки.
	 */
	public registerEventPackage(eventName: string, handler: EventPackageHandler) {
		this.eventPackages.set(normalizeEventKey(eventName), handler);
	}

	/**
	 * Удаляет обработчик ранее зарегистрированного event-package.
	 *
	 * @param eventName Имя event-package.
	 */
	public unregisterEventPackage(eventName: string) {
		this.eventPackages.delete(normalizeEventKey(eventName));
	}

	/* -------------- PRIVATE API -------------- */
	/**
	 * Подключает системные события UA и маршрутизирует их во внутренний EventEmitter.
	 *
	 * @param autoReconnect Флаг автоматического восстановления транспорта.
	 */
	private bindUaEvents(autoReconnect = false): void {
		this.ua.on('registered', () => {
			this.emit('registered');
			const uaUri = (this.ua as any).configuration.uri as JsSIP.URI; // JsSIP.URI
			const selfAor = uaUri.toString();
			const parkUri = JsSIP.URI.parse(DEFAULT_PARK_URI);

			this.watchRegister(selfAor, {
				expires: 3600,
				autoRetry: true,
				retryDelayMs: 5000,
			});

			if (!this.config.disableCallParkingSubscription) {
				this.watchCallParking(selfAor, {
					expires: 600,
					params: {
						from_uri: uaUri,
						to_uri: parkUri,
					},
				});
			}
		});
		this.ua.on('unregistered', () => {
			this.emit('unregistered');
		});
		this.ua.on('registrationFailed', (e) => this.emit('registrationFailed', e));
		// this.ua.on('registrationExpiring', () => this.emit('registrationExpiring'));

		this.ua.on('newRTCSession', (e: any) => {
			const { session, request } = e;
			const isReplaces = Boolean(request) && 'replaces' in request;
			this.initializeSessionInfo(session, isReplaces, request);
		});

		this.ua.on('disconnected', () => {
			console.warn('[UA] WebSocket disconnected');
			if (!autoReconnect && !this.destroyed) {
				this.ua.stop();
			}
		});

		this.ua.on('sipEvent', () => {});
		this.ua.on('newMessage', () => {});
		this.ua.on('newOptions', () => {});

		(this.ua as any).on('subscribe', (data: any) => {
			const req = data?.request ?? data; // на всякий случай
			this.emit('incomingSubscribe', req);
		});
		(this.ua as any).on('notify', (data: any) => {
			this.handleIncomingNotify(data?.request ?? data);
		});

		this.on('subscribeAccepted', (meta: SipEventMeta, sub: any) => {
			const h = this.eventPackages.get(normalizeEventKey(meta.eventName));
			h?.onAccepted?.(meta, sub);
		});
		this.on('subscribeActive', (meta: SipEventMeta) => {
			const h = this.eventPackages.get(normalizeEventKey(meta.eventName));
			h?.onActive?.(meta);
		});
		this.on('subscribePending', (meta: SipEventMeta) => {
			const h = this.eventPackages.get(normalizeEventKey(meta.eventName));
			h?.onPending?.(meta);
		});
		this.on(
			'subscribeTerminated',
			(meta: SipEventMeta, info: SubscriptionTerminatedPayload, sub: any) => {
				const h = this.eventPackages.get(normalizeEventKey(meta.eventName));
				h?.onTerminated?.(meta, info, sub);

				const normalizedTarget = this.normalizeAor(meta.target);
				const key = `${normalizedTarget}|${meta.eventName}`;
				this.subscribers.delete(key);

				if (!shouldRetrySubscriptionTermination(info)) {
					// не пытаемся переподписаться
					return;
				}

				this.scheduleResubscribe(normalizedTarget, meta.eventName, info);
			}
		);
		this.on('notifyReceived', (meta: SipEventMeta, payload: NotifyPayload, sub: any) => {
			const h = this.eventPackages.get(normalizeEventKey(meta.eventName));
			h?.onNotify?.(meta, payload, sub);
		});
	}

	/** Регистрирует стандартные обработчики event-package (reg, parking, conference). */
	private bindPackagesEvent() {
		registerRegPackage(this);
		registerCallParkingPackage(this);
		registerConferencePackage(this);
	}

	/**
	 * Привязывает события JsSIP.Subscriber к внутренним событиям адаптера.
	 *
	 * @param sub Экземпляр подписчика JsSIP.
	 * @param meta Метаданные подписки.
	 */
	private bindSubscriberEvents(sub: any, meta: { target: string; eventName: string }) {
		sub.on('accepted', () => this.emit('subscribeAccepted', meta, sub));
		sub.on('active', () => this.emit('subscribeActive', meta));
		sub.on('pending', () => this.emit('subscribePending', meta));
		sub.on('terminated', (code: number, reason?: string, retryAfter?: number) =>
			this.emit('subscribeTerminated', meta, { code, reason, retryAfter }, sub)
		);
		sub.on('notify', (isFinal: boolean, request: any, body: string | null, contentType?: string) =>
			this.emit('notifyReceived', meta, { isFinal, request, body, contentType }, sub)
		);
	}

	/**
	 * Привязывает события Notifier к внутренним событиям адаптера.
	 *
	 * @param notifier Экземпляр JsSIP Notifier.
	 */
	private bindNotifierEvents(notifier: any) {
		notifier.on('subscribe', (res: any) => this.emit('notifySent', res, notifier));
		notifier.on('terminated', (e: any) => this.emit('notifierTerminated', e, notifier));
	}

	/**
	 * Отменяет запланированный таймер повторной подписки по ключу.
	 *
	 * @param key Ключ вида `<target>|<eventName>`.
	 */
	private clearResubscribeTimer(key: string) {
		const timer = this.resubscribeTimers.get(key);
		if (!timer) return;
		clearTimeout(timer);
		this.resubscribeTimers.delete(key);
	}

	/**
	 * Планирует повторную подписку с учётом retry-after и user-предпочтений.
	 *
	 * @param target Нормализованный AOR цели.
	 * @param eventName Имя event-package.
	 * @param info Детали причины завершения подписки.
	 */
	private scheduleResubscribe(
		target: string,
		eventName: string,
		info: SubscriptionTerminatedPayload
	) {
		const key = `${target}|${eventName}`;
		const pref = this.watchPreferences.get(key);
		if (!pref?.autoRetry || this.destroyed) return;

		const preferredDelay = pref.retryDelayMs ?? 5000;
		const retryAfterMs =
			typeof info.retryAfter === 'number' && info.retryAfter >= 0
				? info.retryAfter * 1000
				: preferredDelay;
		const delay = Math.max(500, retryAfterMs);

		this.clearResubscribeTimer(key);

		const timer = setTimeout(() => {
			this.resubscribeTimers.delete(key);

			if (this.destroyed) return;

			const latestPref = this.watchPreferences.get(key);
			if (!latestPref?.autoRetry) return;

			if (!this.ua.isRegistered()) {
				this.scheduleResubscribe(target, eventName, {
					code: info.code,
					reason: info.reason,
					retryAfter: (latestPref.retryDelayMs ?? preferredDelay) / 1000,
				});
				return;
			}

			try {
				this.subscribe(target, eventName, latestPref.accept, latestPref.options);
			} catch (error) {
				console.error(`[UA] Resubscribe for ${eventName} failed`, error);
				this.scheduleResubscribe(target, eventName, {
					code: info.code,
					reason: info.reason,
					retryAfter: (latestPref.retryDelayMs ?? preferredDelay) / 1000,
				});
			}
		}, delay);

		this.resubscribeTimers.set(key, timer);
	}

	/**
	 * Создаёт внутреннюю модель сессии и подключает её обработчики.
	 *
	 * @param session RTCSession от JsSIP.
	 * @param isReplaces Признак, что диалог создан по Replaces.
	 * @param request Исходный SIP-запрос для этой сессии.
	 */
	private initializeSessionInfo(session: RTCSession, isReplaces: boolean, request?: any): void {
		const { isConference } = parseContactUriFromResponse(request);

		const remoteUser = session.remote_identity.uri.toString();
		const isParked = remoteUser === DEFAULT_PARK_URI;
		const referredBy = getIdentityFromHeader(request, 'Referred-By');
		const displayName = referredBy.displayName || session?.remote_identity?.display_name?.trim?.();

		const info: SessionInfo = {
			id: session.id,
			session,
			isOnHold: {
				local: false,
				remote: false,
			},
			isParked,
			isReplaces,
			isConference,
			localStream: new MediaStream(),
			remoteStream: new MediaStream(),
			aor: remoteUser,
			displayName,
		};
		this.sessions.set(info.id, info);
		patchSession(info);

		this.emit('sessionCreated', info);
		this.bindSessionEvents(info);
	}

	/**
	 * Подключает обработчики SIP/WebRTC событий к конкретной сессии.
	 *
	 * @param info Внутреннее описание сессии.
	 */
	private bindSessionEvents(info: SessionInfo): void {
		const { session } = info;
		const earlyIceReadyController = createEarlyIceReadyController();

		// Additional SIP dialog events
		session.on('icecandidate', earlyIceReadyController.handleIceCandidate);
		session.on('reinvite', (evt: ReInviteEvent) => {
			const sdp = evt.request.body;

			this.handleRemoteSDP(info, sdp);
		});
		session.on('reinviteRequestPending', ({ retryAfterMs }: ReinviteRequestPendingEvent) => {
			this.handleReinvitePending(info, retryAfterMs);
		});

		session.on('update', (e: any): void => {
			this.emit('update', info.id, e);
		});
		session.on('replaces', (payload: any): void => {
			this.handleIncomingReplaces(payload);
		});
		session.on('sdp', (evt: any): void => {
			if (evt.originator === 'local') {
				// if (info.isOnHold.local) {
				//     evt.sdp = sdpModifier(evt.sdp);
				// }
			}
			if (evt.originator === 'remote') {
				this.emitRemoteStatus(evt.sdp, info);
			}

			this.emit('sdp', info.id, evt);
		});
		session.on('connecting', () => {});
		session.on('progress', (): void => {
			this.emit('progress', info);
		});
		session.on('accepted', (): void => {
			this.emit('accepted', info);
		});
		session.on('confirmed', () => {
			const pc = session.connection;
			this.attachMediaHandlers(info, pc);
		});

		session.on('ended', (): void => {
			earlyIceReadyController.cancel();
			// const isRemoteBye = e.originator === 'remote' && e.cause === JsSIP.C.causes.BYE;

			// фиксируем, кем завершён звонок
			this.cleanupSession(info.id);
			this.emit('ended', info);
		});
		session.on('failed', (e: any): void => {
			earlyIceReadyController.cancel();
			// Если сервер вернул 404 или 5xx — эмитим callError
			const code = e.message?.status_code;
			const message = e.message?.reason_phrase || e.cause || e.message;

			if (code && code >= 300) {
				if (code === 302) {
					const { uri, isConference } = parseContactUriFromResponse(e.message);
					if (uri) {
						this.emit('redirect', uri, isConference);
					}
				} else {
					this.emit('callError', info.id, code, message);
				}
			}
			// Обычная очистка сессии
			this.cleanupSession(info.id);
			this.emit('failed', info, e);
		});

		session.on('peerconnection', (e: any): void => {
			const pc: RTCPeerConnection = e.peerconnection;

			this.attachMediaHandlers(info, pc);
		});
		session.on('refer', (request: ReferEvent) => {
			const sipRequest = request?.request;
			if (!sipRequest) return;

			const { aor, displayName } = getIdentityFromHeader(sipRequest, 'Refer-To');
			if (!aor) return;

			const payload: SipTarget = {
				aor,
				displayName,
			};

			// 4) Новый вызов Transferee → Target
			this.emit('initReferCall', payload);
		});
	}

	/**
	 * Обрабатывает входящий REFER с Replaces и автоматически принимает его.
	 *
	 * @param payload Payload события `replaces` от JsSIP.
	 */
	private handleIncomingReplaces(payload: ReferEvent) {
		const { accept, reject, request } = payload;

		if (!request) {
			console.warn('[Replaces] invalid payload received');
			return;
		}

		try {
			accept((newSession: RTCSession) => {
				this.emit('initParkCall', newSession.id);
			});
		} catch (error) {
			console.warn('[Replaces] auto-accept failed', error);
			reject();
		}
	}

	/**
	 * Подключает/синхронизирует локальные и удалённые медиатрекы сессии.
	 *
	 * @param info Внутреннее описание сессии.
	 * @param pc RTCPeerConnection текущей сессии.
	 */
	private attachMediaHandlers(info: SessionInfo, pc: RTCPeerConnection) {
		// Local senders
		pc.getSenders().forEach(({ track }) => {
			if (track && !info.localStream.getTrackById(track.id)) {
				info.localStream.addTrack(track);
			}
		});
		if (info.localStream.getTracks().length > 0) {
			this.emit('localStream', info.localStream);
		}

		const subscribeRemoteStream = (track: MediaStreamTrack) => {
			if (track.kind === 'audio') {
				// сбрасываем старые
				info.remoteStream.getAudioTracks().forEach((t) => info.remoteStream.removeTrack(t));
				// добавляем новый
				info.remoteStream.addTrack(track);
				this.emit('remoteStream', info.remoteStream);
			}
			if (track.kind === 'video') {
				// сбрасываем старые
				info.remoteStream.getVideoTracks().forEach((t) => info.remoteStream.removeTrack(t));

				// добавляем новый
				info.remoteStream.addTrack(track);
				this.emit('remoteStream', info.remoteStream);
			}
		};
		// remote receivers
		pc.getReceivers().forEach((evt) => {
			subscribeRemoteStream(evt.track);
		});

		// Получаем треки, если обновились в моменте звонка
		pc.addEventListener('track', (evt: RTCTrackEvent) => {
			subscribeRemoteStream(evt.track);

			evt.receiver.track.addEventListener('unmute', () => {
				// this.emit('remoteStream', info.id, new MediaStream([evt.receiver.track]))
			});
			evt.receiver.track.addEventListener('mute', () => {
				// this.emit('remoteStream', info.id, new MediaStream())
			});
			evt.track.addEventListener('ended', () => {
				// info.remoteStream.removeTrack(e.target);
				// this.emit('remoteStream', info.remoteStream);
			});
		});

		// 3) Ловим состояния ICE для дебага
		pc.addEventListener('icegatheringstatechange', () =>
			this.emit('iceGatheringStateChange', info.id, pc.iceGatheringState)
		);
		pc.addEventListener('iceconnectionstatechange', () =>
			this.emit('iceConnectionStateChange', info.id, pc.iceConnectionState)
		);
	}

	/**
	 * Вычисляет mute-состояния удалённой стороны из SDP и эмитит их в UI.
	 *
	 * @param sdp SDP удалённой стороны.
	 * @param info Внутреннее описание сессии.
	 */
	private emitRemoteStatus = (sdp: string, info: SessionInfo) => {
		const audioDir = getDirection(sdp, 'audio');
		const videoDir = getDirection(sdp, 'video');

		/* ---------- AUDIO ---------- */
		const audioMuted = audioDir === 'recvonly' || audioDir === 'inactive';
		this.emit('remoteAudioMuted', info.id, audioMuted);

		/* ---------- VIDEO ---------- */
		// muted == нет m=video ИЛИ direction inact/recvonly
		const videoMuted = !videoDir || videoDir === 'recvonly' || videoDir === 'inactive';
		this.emit('remoteVideoMuted', info.id, videoMuted);
	};

	/**
	 * Восстанавливает локальные треки после выхода из hold-состояния.
	 *
	 * @param info Внутреннее описание сессии.
	 */
	private restoreTracks = (info: SessionInfo) => {
		const pc = info.session.connection;

		const { audio: audioMuted } = info.session.isMuted();

		// AUDIO
		const audioTrack = info.localStream.getAudioTracks()[0];
		if (audioMuted) {
			pc.addTransceiver(audioTrack, { direction: 'recvonly' });
			info.session.mute({ audio: true });
		} else {
			// свой флаг «микрофон выключен?»
			pc.addTransceiver(audioTrack, { direction: 'sendrecv' });
			info.session.unmute({ audio: true });
		}

		// VIDEO
		const videoTrack = info.localStream.getVideoTracks()[0];
		if (videoTrack) {
			pc.addTransceiver('video', { direction: 'recvonly' });
			info.session.mute({ video: true });
		}

		this.emit('localStream', info.localStream); // UI покажет «выкл.»
	};

	/**
	 * Ретраит renegotiate после `reinviteRequestPending` (обычно 491).
	 *
	 * @param info Внутреннее описание сессии.
	 * @param retryAfterMs Подсказка задержки от стека/сервера.
	 */
	private handleReinvitePending(info: SessionInfo, retryAfterMs?: number) {
		const delay = Number.isFinite(retryAfterMs) ? Math.max(0, Number(retryAfterMs)) : 0;
		const maxRetries = 5;
		const retryGapMs = 200;

		const scheduleRetry = (nextDelay: number, attemptsLeft: number) => {
			const prevTimer = this.reinviteRetryTimers.get(info.id);
			if (prevTimer) clearTimeout(prevTimer);

			const timer = setTimeout(async () => {
				this.reinviteRetryTimers.delete(info.id);

				const { session } = info;
				if (this.destroyed || session.isEnded?.()) return;

				const readyToReOffer =
					typeof (session as any)._isReadyToReOffer === 'function'
						? (session as any)._isReadyToReOffer()
						: session.isEstablished?.();

				if (!readyToReOffer) {
					if (attemptsLeft > 0) scheduleRetry(retryGapMs, attemptsLeft - 1);
					return;
				}

				try {
					applyDirectionalPolicy(info);

					const retryOptions = {
						useUpdate: false,
						rtcOfferConstraints: { iceRestart: true },
					};

					const result = await session.renegotiate(retryOptions);
					if (result === false) {
						if (attemptsLeft > 0) scheduleRetry(retryGapMs, attemptsLeft - 1);
						return;
					}
					info.pendingRemoteDir = undefined;
				} catch (error) {
					console.warn('[ReINVITE 491] renegotiate retry failed', error);
					if (attemptsLeft > 0) scheduleRetry(retryGapMs, attemptsLeft - 1);
				}
			}, nextDelay);

			this.reinviteRetryTimers.set(info.id, timer);
		};

		scheduleRetry(delay, maxRetries);
	}

	/**
	 * Обрабатывает удалённый SDP: обновляет hold и mute-состояния.
	 *
	 * @param info Внутреннее описание сессии.
	 * @param sdp SDP от удалённой стороны.
	 */
	private handleRemoteSDP(info: SessionInfo, sdp: string) {
		this.emitRemoteStatus(sdp, info);

		/* ----- HOLD: если порт = 0 ---------- */
		const remoteHold = isPortZero(sdp);

		if (remoteHold) {
			this.emit('remoteHold', info.id, true);

			info.session.mute({ video: true });
			this.emit('localStream', info.localStream); // UI покажет «вы

			info.isOnHold.remote = true;
		} else {
			this.emit('remoteHold', info.id, false);

			const pc = info.session.connection;

			if (pc.getTransceivers().length === 0) {
				this.restoreTracks(info);
			}

			info.isOnHold.remote = false;
		}
	}

	/**
	 * Отправляет standalone REFER (новый диалог с новым Call-ID).
	 *
	 * @param requestUri Куда отправляем REFER (обычно URI фокуса).
	 * @param extraHeaders Дополнительные SIP-заголовки (включая Refer-To).
	 * @param options Дополнительные колбэки и настройки.
	 */
	private sendStandaloneRefer(
		requestUri: string,
		extraHeaders: string[],
		options?: {
			onTrying?: (code?: number, reason?: string) => void;
			onProgress?: (code?: number, reason?: string) => void;
			onAccepted?: (code?: number, reason?: string) => void;
			onFailed?: (code?: number, reason?: string) => void;
			onTimeout?: () => void;
			onTransportError?: () => void;
			beforeSend?: (request: any) => void;
		}
	) {
		let parsedUri: JsSIP.URI | null = null;
		try {
			parsedUri = JsSIP.URI.parse(this.normalizeAor(requestUri));
		} catch {
			console.warn('[REFER] invalid request URI', requestUri);
			return;
		}
		if (!parsedUri) return;

		const uaConfig = (this.ua as any).configuration ?? {};

		const headers = [...extraHeaders];

		const requestParams: Partial<RequestParams> = {
			from_uri: uaConfig.uri,
			from_display_name: uaConfig.display_name,
			to_uri: parsedUri,
		};

		const request = new OutgoingRequest('REFER', parsedUri, this.ua, requestParams, headers);
		options?.beforeSend?.(request);

		const sender = new RequestSender(this.ua, request, {
			onRequestTimeout: () => {
				console.warn('[REFER] timeout', request.call_id);
				options?.onTimeout?.();
			},
			onTransportError: () => {
				console.warn('[REFER] transport error', request.call_id);
				options?.onTransportError?.();
			},
			onReceiveResponse: (response) => {
				if (!response) return;
				const status = response.status_code;
				if (status >= 300) {
					console.warn('[REFER] failed', status, response.reason_phrase);
					options?.onFailed?.(status, response.reason_phrase);
				} else if (status >= 200) {
					options?.onAccepted?.(status, response.reason_phrase);
				} else if (status >= 180) {
					options?.onProgress?.(status, response.reason_phrase);
				} else if (status >= 100) {
					options?.onTrying?.(status, response.reason_phrase);
				}
			},
		});

		try {
			sender.send();
		} catch (error) {
			console.warn('[REFER] send failed', error);
			options?.onFailed?.(undefined, (error as Error)?.message);
		}

		return request.call_id;
	}

	/**
	 * Обрабатывает входящий NOTIFY для исходящих REFER-запросов.
	 *
	 * @param requestLike Входящий NOTIFY (или обёртка над ним).
	 */
	private handleIncomingNotify(requestLike: any) {
		// TODO Поправить типы
		const request: SipRequest | undefined = requestLike?.request ?? requestLike;
		// TODO Упростить получение getHeader
		if (!request?.getHeader) return;
		const eventHeader = request.getHeader('Event') ?? request.getHeader('event');
		if (!eventHeader) return;

		const eventName = eventHeader.split(';')[0].trim().toLowerCase();
		if (eventName !== 'refer') return;

		const callId =
			request.getHeader('Call-ID') ?? request.getHeader('call-id') ?? (request as any)?.call_id;
		if (!callId) return;

		(request as any)[NOTIFY_HANDLED_FLAG] = true;
		try {
			request.reply?.(200);
		} catch (error) {
			console.warn('[REFER notify] ack failed', error);
		}

		const meta = this.parkSubscriptions.get(callId);

		if (meta) {
			this.processReferNotify(String(callId), request, meta);
			return;
		}

		const referMeta = this.referSubscriptions.get(callId);
		if (referMeta) {
			this.processGenericReferNotify(String(callId), request, referMeta);
		}
	}

	/**
	 * Обрабатывает NOTIFY по парковочному REFER и эмитит park-события.
	 *
	 * @param callId Call-ID REFER-запроса.
	 * @param request SIP NOTIFY-запрос.
	 * @param meta Метаданные парковочной операции.
	 */
	private processReferNotify(
		callId: string,
		// TODO Поправить типы
		request: SipRequest,
		meta: { sessionId: string; target: string }
	) {
		const status = parseSipfragStatus(request.body);
		// TODO Упростить получение getHeader
		const subsHeader =
			request.getHeader('Subscription-State') ?? request.getHeader('subscription-state');
		const subscriptionState = parseSubscriptionState(subsHeader);

		if (status) {
			if (status.code >= 300) {
				this.emitParkFailure(meta.sessionId, {
					code: status.code,
					reason: status.reason,
				});
				this.parkSubscriptions.delete(callId);
				return;
			}

			const stage = status.code < 200 ? (status.code === 100 ? 'trying' : 'progress') : 'accepted';

			this.emitParkProgress(meta.sessionId, {
				stage,
				code: status.code,
				reason: status.reason,
			});
		}

		if (subscriptionState?.state === 'terminated') {
			const reason = subscriptionState.reason.toLowerCase();
			if (status && status.code >= 200 && status.code < 300) {
				this.emitParkProgress(meta.sessionId, {
					stage: 'succeeded',
					code: status.code,
					reason: status.reason,
				});
			} else if (reason === 'noresource') {
				this.emitParkProgress(meta.sessionId, {
					stage: 'succeeded',
					reason: subscriptionState.reason,
				});
			} else {
				this.emitParkFailure(meta.sessionId, {
					code: status?.code,
					reason: subscriptionState.reason || status?.reason || 'Call park terminated',
				});
			}
			this.parkSubscriptions.delete(callId);
		}
	}

	/**
	 * Обрабатывает NOTIFY по обычному REFER (без парковки).
	 *
	 * @param callId Call-ID REFER-запроса.
	 * @param request SIP NOTIFY-запрос.
	 * @param meta Метаданные REFER-подписки.
	 */
	private processGenericReferNotify(
		callId: string,
		// TODO Поправить типы
		request: SipRequest,
		meta: { requestUri: string; referTo: SipTarget }
	) {
		const status = parseSipfragStatus(request.body);
		// TODO Упростить получение getHeader
		const subsHeader =
			request.getHeader('Subscription-State') ?? request.getHeader('subscription-state');
		const subscriptionState = parseSubscriptionState(subsHeader);

		if (status) {
			this.emit('referNotify', {
				callId,
				code: status.code,
				reason: status.reason,
				requestUri: meta.requestUri,
				referTo: meta.referTo,
				subscriptionState,
			});
		}

		const isFinalCode = Boolean(status?.code && status.code >= 200);
		if (subscriptionState?.state === 'terminated' || isFinalCode) {
			this.referSubscriptions.delete(callId);
		}
	}

	/**
	 * Формирует и отправляет событие прогресса парковки.
	 *
	 * @param id Идентификатор звонка/сессии.
	 * @param data Частичные данные прогресса.
	 */
	private emitParkProgress(id: string, data: Partial<ParkProgressEvent>) {
		const payload: ParkProgressEvent = {
			id,
			stage: data.stage ?? 'progress',
			code: data.code,
			reason: data.reason,
		};
		this.emit('parkProgress', payload);
	}

	/**
	 * Формирует и отправляет событие ошибки парковки.
	 *
	 * @param id Идентификатор звонка/сессии.
	 * @param data Данные ошибки.
	 */
	private emitParkFailure(id: string, data: { code?: number; reason?: string }) {
		const payload: ParkFailureEvent = {
			id,
			code: data.code,
			reason: data.reason ?? 'Call parking failed',
		};
		this.emit('parkFailed', payload);
	}

	/**
	 * Очищает обработчики/таймеры сессии и завершает её при необходимости.
	 *
	 * @param id Идентификатор сессии.
	 */
	private cleanupSession(id: string): void {
		const info = this.sessions.get(id);
		if (!info) return;

		info.session.removeAllListeners();
		const retryTimer = this.reinviteRetryTimers.get(id);
		if (retryTimer) {
			clearTimeout(retryTimer);
			this.reinviteRetryTimers.delete(id);
		}

		// Завершаем только если диалог всё ещё активен
		if (info.session.isEstablished() || info.session.isInProgress()) {
			info.session.terminate();
		}

		this.sessions.delete(id);
	}

	/**
	 * Возвращает Contact URI текущего UA.
	 *
	 * @returns Строка формата `<sip:user@host;gr=...>`.
	 */
	private getContactUri(): string {
		return this.ua.contact?.toString?.();
	}

	/** Освобождает все ресурсы адаптера и останавливает UA. */
	private destroy(): void {
		this.destroyed = true;
		// Завершаем подписки/нотификаторы
		this.subscribers.forEach((sub) => {
			sub.terminate?.();
		});
		this.subscribers.clear();
		this.notifiers.forEach((n) => {
			n.terminate?.();
		});
		this.notifiers.clear();
		this.resubscribeTimers.forEach((timer) => clearTimeout(timer));
		this.reinviteRetryTimers.forEach((timer) => clearTimeout(timer));
		this.reinviteRetryTimers.clear();
		this.resubscribeTimers.clear();
		this.watchPreferences.clear();
		this.parkSubscriptions.clear();

		this.sessions.forEach((i) => {
			i.session.removeAllListeners();
			i.session.terminate();
		});
		this.regState.clear();
		this.eventPackages.clear(); // если пакеты регистрируются динамически
		this.sessions.clear();
		this.ua.removeAllListeners();
		this.ua.stop();
		this.removeAllListeners();
	}
}
